import React, { useState, useEffect } from 'react'
import DOMPurify from 'dompurify'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  IconButton,
  Chip,
  Paper,
  Divider
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Save as SaveIcon,
  SmartToy as SmartToyIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import { apiService } from '../../services/api'
import { useUser } from '../../context/UserContext'

// All available operators
const ALL_OPERATORS = ['ORANGE', 'PLUS', 'TMOBILE', 'P4']

// Helpers to align operator presentation with Overview
const getOperatorColor = (code) => {
  const colorMap = {
    'ORANGE': '#FF7900',
    'PLUS': '#00A651',
    'TMOBILE': '#E20074',
    'P4': '#662583'
  }
  return colorMap[code] || '#6b7280'
}

const getOperatorLabel = (code) => {
  const labelMap = {
    'ORANGE': 'Orange',
    'PLUS': 'Plus',
    'TMOBILE': 'T-Mobile',
    'P4': 'Play'
  }
  return labelMap[code] || code
}

const AIAnalysis = () => {
  const { getAccessibleOperators } = useUser()
  
  // Filter operators based on user access
  const accessibleOperators = getAccessibleOperators(ALL_OPERATORS)
  
  // Set initial operator to first accessible operator
  const [selectedOperator, setSelectedOperator] = useState(accessibleOperators[0] || 'ORANGE')
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [currentAnalysis, setCurrentAnalysis] = useState(null)
  const [savedAnalyses, setSavedAnalyses] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [analysisFilter, setAnalysisFilter] = useState('Wszystkie')
  const [analysisLimit, setAnalysisLimit] = useState(20)
  const [notesChanges, setNotesChanges] = useState({})

  // Load summary and analyses on component mount
  useEffect(() => {
    loadSummary()
    loadSavedAnalyses()
  }, [analysisFilter, analysisLimit])

  const loadSummary = async () => {
    try {
      const response = await apiService.getAnalysesSummary()
      setSummary(response.data)
    } catch (err) {
      console.error('Error loading summary:', err)
    }
  }

  const loadSavedAnalyses = async () => {
    setLoading(true)
    setError(null)
    try {
      const operatorCode = analysisFilter === 'Wszystkie' ? null : analysisFilter
      const response = await apiService.getSavedAnalyses(operatorCode, analysisLimit)
      setSavedAnalyses(response.data.analyses || [])
    } catch (err) {
      setError(`Błąd ładowania analiz: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleAnalyze = async () => {
    setIsAnalyzing(true)
    setError(null)
    setCurrentAnalysis(null)

    try {
      const response = await apiService.analyzeOperatorWithAI(selectedOperator)
      setCurrentAnalysis(response.data)
      // Refresh the saved analyses list
      await loadSavedAnalyses()
      await loadSummary()
    } catch (err) {
      setError(`Błąd analizy AI: ${err.message}`)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę analizę?')) return

    try {
      await apiService.deleteSavedAnalysis(analysisId)
      await loadSavedAnalyses()
      await loadSummary()
    } catch (err) {
      setError(`Błąd usuwania: ${err.message}`)
    }
  }

  const handleNotesChange = (analysisId, newNotes) => {
    setNotesChanges(prev => ({
      ...prev,
      [analysisId]: newNotes
    }))
  }

  const handleSaveNotes = async (analysisId) => {
    const newNotes = notesChanges[analysisId]
    if (newNotes === undefined) return

    try {
      await apiService.updateAnalysisNotes(analysisId, newNotes)
      // Remove from changes tracking
      setNotesChanges(prev => {
        const updated = { ...prev }
        delete updated[analysisId]
        return updated
      })
      await loadSavedAnalyses()
    } catch (err) {
      setError(`Błąd zapisywania notatek: ${err.message}`)
    }
  }

  const handleCopyHTML = async (htmlContent) => {
    try {
      await navigator.clipboard.writeText(htmlContent)
      // Show temporary success (you could add a snackbar here)
      console.log('HTML copied to clipboard')
    } catch (err) {
      console.error('Failed to copy HTML:', err)
    }
  }

  const handleDownloadHTML = (analysis) => {
    const timestamp = analysis.timestamp.substring(0, 19).replace('T', '_').replace(/:/g, '-')
    const filename = `analiza_${analysis.operator_code}_${timestamp}.html`
    
    const completeHTML = `<!DOCTYPE html>
<html lang="pl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Analiza AI ${analysis.operator_code} - ${timestamp}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; background: #f5f5f5; }
        .container { max-width: 1200px; margin: 0 auto; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px; }
        .metadata { background: #f8fafc; padding: 15px; border-radius: 5px; margin-bottom: 20px; font-size: 14px; }
        .analysis-content { line-height: 1.6; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🤖 Analiza AI - ${analysis.operator_code}</h1>
            <p><strong>Data wykonania:</strong> ${timestamp.replace('_', ' ').replace(/-/g, ':')}</p>
            <p><strong>ID analizy:</strong> ${analysis.id}</p>
        </div>
        <div class="metadata">
            <strong>📊 Metadane analizy:</strong><br>
            • Operator: ${analysis.operator_code}<br>
            • Przeanalizowane salony: ${analysis.data_stats?.salons_analyzed || 'N/A'}<br>
            • Przeanalizowane wizyty: ${analysis.data_stats?.visits_analyzed || 'N/A'}<br>
            • Całkowita liczba salonów: ${analysis.data_stats?.total_salons || 'N/A'}
        </div>
        <div class="analysis-content">
            ${analysis.analysis_html || ''}
        </div>
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
            Wygenerowano przez KAM-SFA Reporting - Analiza AI
        </div>
    </div>
</body>
</html>`

    const blob = new Blob([completeHTML], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            fontWeight: 500,
            color: 'primary.main'
          }}
        >
          <SmartToyIcon sx={{ fontSize: 28 }} />
          Analiza AI
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Dashboard do analizy wydajności KAM i operatorów telekomunikacyjnych
        </Typography>
      </Box>

      {/* Main Analysis Section */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              fontSize: '0.9rem',
              '& .MuiAlert-icon': { fontSize: 24 }
            }}
            icon={<SmartToyIcon />}
          >
            <strong>Funkcja eksperymentalna:</strong> Analiza sprzedaży operatorów przy użyciu Gemini AI z danymi szczegółowymi
          </Alert>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="large">
                <InputLabel sx={{ fontSize: '1rem' }}>Wybierz operatora</InputLabel>
                <Select
                  value={selectedOperator}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  label="Wybierz operatora"
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiSelect-select': { fontSize: '1rem' }
                  }}
                >
                  {accessibleOperators.map(operatorCode => (
                    <MenuItem 
                      key={operatorCode}
                      value={operatorCode} 
                      sx={{ fontSize: '1rem', color: getOperatorColor(operatorCode), fontWeight: 600 }}
                    >
                      {getOperatorLabel(operatorCode)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={7}>
              <Button
                variant="contained"
                size="medium"
                fullWidth
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                startIcon={isAnalyzing ? <CircularProgress size={24} color="inherit" /> : <AnalyticsIcon />}
                sx={{ 
                  py: 1.25,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                {isAnalyzing ? 'Wykonuję analizę AI...' : `Rozpocznij analizę AI — ${getOperatorLabel(selectedOperator)}`}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Current Analysis Results */}
          {currentAnalysis && (
            <Box sx={{ mt: 4 }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontWeight: 600,
                  color: 'text.primary'
                }}
              >
                Wyniki analizy AI
              </Typography>
              
              {currentAnalysis.analysis_id && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    fontSize: '0.9rem'
                  }}
                >
                  Analiza została zapisana w bazie (ID: <strong>{currentAnalysis.analysis_id}</strong>)
                </Alert>
              )}

              {/* Render HTML content */}
              {currentAnalysis.ai_insights_html && (
                <Paper 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    maxHeight: 700, 
                    overflow: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentAnalysis.ai_insights_html) }} />
                </Paper>
              )}

              {/* Analysis stats */}
              <Accordion 
                sx={{ 
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    backgroundColor: 'action.hover',
                    borderRadius: '8px 8px 0 0'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Statystyki analizy
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {currentAnalysis.data_stats?.visits_analyzed || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Wizyty</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {currentAnalysis.data_stats?.salons_analyzed || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Salony</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {currentAnalysis.data_stats?.total_salons || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Wszystkie salony</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">
                          {currentAnalysis.analysis_id || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">ID analizy</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Saved Analyses History */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            color: 'primary.main'
          }}
        >
          <HistoryIcon />
          Historia zapisanych analiz
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Przeglądaj wcześniejsze analizy AI i zarządzaj zapisanymi raportami
        </Typography>
      </Box>

      {/* Summary Stats */}
      {summary && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Podsumowanie statystyk
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {summary.successful_analyses || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Łącznie analiz
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {summary.recent_analyses_7d || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Ostatnie 7 dni
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {summary.by_operator?.ORANGE || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Orange
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {summary.by_operator?.PLUS || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Plus
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2, backgroundColor: 'background.default' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Filtry i opcje
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel>Filtruj po operatorze</InputLabel>
                <Select
                  value={analysisFilter}
                  onChange={(e) => setAnalysisFilter(e.target.value)}
                  label="Filtruj po operatorze"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Wszystkie">Wszyscy operatorzy</MenuItem>
                  {accessibleOperators.map(operatorCode => (
                    <MenuItem 
                      key={operatorCode}
                      value={operatorCode} 
                      sx={{ color: getOperatorColor(operatorCode), fontWeight: 600 }}
                    >
                      {getOperatorLabel(operatorCode)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                label="Liczba analiz"
                value={analysisLimit}
                onChange={(e) => setAnalysisLimit(Number(e.target.value))}
                inputProps={{ min: 5, max: 100, step: 5 }}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                fullWidth
                onClick={loadSavedAnalyses}
                startIcon={<RefreshIcon />}
                disabled={loading}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Odśwież listę
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Analyses List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : savedAnalyses.length === 0 ? (
        <Alert severity="info">
          🔍 Brak zapisanych analiz do wyświetlenia
        </Alert>
      ) : (
        <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          Znaleziono {savedAnalyses.length} analiz:
        </Typography>

          {savedAnalyses.map((analysis) => {
            const timestamp = analysis.timestamp.substring(0, 19).replace('T', ' ')
            const hasNotesChanges = notesChanges[analysis.id] !== undefined
            const currentNotes = hasNotesChanges ? notesChanges[analysis.id] : (analysis.notes || '')

            return (
              <Accordion 
                key={analysis.id} 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    backgroundColor: 'action.hover',
                    borderRadius: '8px 8px 0 0',
                    '&:hover': {
                      backgroundColor: 'action.focus'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      #{analysis.id} - {timestamp}
                    </Typography>
                    {(() => {
                      const color = getOperatorColor(analysis.operator_code)
                      const label = getOperatorLabel(analysis.operator_code)
                      return (
                        <Chip label={label} sx={{ fontWeight: 600, bgcolor: color, color: 'white' }} />
                      )
                    })()}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Analysis Info */}
                    <Grid item xs={12} md={8}>
                      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          <strong>Operator:</strong> {getOperatorLabel(analysis.operator_code)}<br />
                          <strong>📅 Data:</strong> {timestamp}<br />
                          <strong>🏪 Przeanalizowane salony:</strong> {analysis.data_stats?.salons_analyzed || 'N/A'}<br />
                          <strong>📋 Wizyty:</strong> {analysis.data_stats?.visits_analyzed || 'N/A'}
                        </Typography>
                      </Paper>

                      {/* Notes */}
                      <TextField
                        label="📝 Notatki do analizy"
                        multiline
                        rows={4}
                        fullWidth
                        value={currentNotes}
                        onChange={(e) => handleNotesChange(analysis.id, e.target.value)}
                        sx={{ 
                          mb: 2,
                          '& .MuiOutlinedInput-root': { borderRadius: 2 }
                        }}
                        placeholder="Dodaj swoje notatki i uwagi do tej analizy..."
                      />

                      {hasNotesChanges && (
                        <Button
                          variant="contained"
                          size="medium"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveNotes(analysis.id)}
                          sx={{ 
                            mb: 2,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Zapisz notatki
                        </Button>
                      )}
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                          Akcje
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => handleCopyHTML(analysis.analysis_html)}
                            fullWidth
                            sx={{ 
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600
                            }}
                          >
                            Kopiuj HTML
                          </Button>
                          
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadHTML(analysis)}
                            fullWidth
                            sx={{ 
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600
                            }}
                          >
                            Pobierz HTML
                          </Button>
                          
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteAnalysis(analysis.id)}
                            fullWidth
                            sx={{ 
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600
                            }}
                          >
                            Usuń analizę
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Analysis Preview */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          Podgląd analizy AI
                        </Typography>
                        {analysis.analysis_html ? (
                          <Paper 
                            sx={{ 
                              p: 3, 
                              maxHeight: 500, 
                              overflow: 'auto',
                              borderRadius: 2,
                              boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'background.paper'
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(analysis.analysis_html) }} />
                          </Paper>
                        ) : (
                          <Alert 
                            severity="warning"
                            sx={{ 
                              borderRadius: 2,
                              fontSize: '0.9rem'
                            }}
                          >
                            Brak zawartości analizy — plik może być uszkodzony
                          </Alert>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default AIAnalysis
